import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminAuth } from "../../contexts/AdminAuthContext";
import { clearAdminToken } from "../../auth/adminTokenStore";
import ssoApi from "../../api/sso/ssoApi";
import { completeSsoLogin, INTERNAL_ONLY_MESSAGE, isInternalEmail, SSO_CALLBACK_URL } from "../../auth/sso";
import stytchClient from "../../services/stytchClient";
import colors from "../../designSystem/colors";
import MfaStep, { MfaChallenge } from "../../components/MfaStep";
import CoverflowLogo from "../../assets/icons/CoverflowLogo.svg";
import "./responsive.css";

const { UI } = colors;

type LoginStage = "email" | "methods" | "magic_link_sent" | "otp_verify" | "mfa";
type AuthMethod =
  | "magic_link"
  | "email_otp"
  | "sso"
  | "google_oauth"
  | "microsoft_oauth";

const inputStyle: React.CSSProperties = {
  padding: "11px 14px",
  borderRadius: 10,
  border: `1px solid ${UI.BORDER.STANDARD}`,
  fontSize: 15,
  minHeight: 42,
  color: UI.TEXT.STANDARD,
  background: UI.BACKGROUND.STANDARD,
  boxSizing: "border-box",
  width: "100%",
  fontFamily: "inherit",
};

const primaryButtonStyle: React.CSSProperties = {
  padding: "11px 14px",
  borderRadius: 10,
  fontSize: 15,
  fontWeight: 600,
  minHeight: 42,
  width: "100%",
  border: "none",
  background: UI.TEXT.ACTIVE,
  color: "#fff",
  cursor: "pointer",
  transition: "opacity 0.2s",
};

const secondaryButtonStyle: React.CSSProperties = {
  padding: "11px 14px",
  borderRadius: 10,
  fontSize: 14,
  fontWeight: 500,
  minHeight: 42,
  width: "100%",
  border: `1px solid ${UI.BORDER.STANDARD}`,
  background: "#fff",
  color: UI.TEXT.STANDARD,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  marginBottom: 10,
  boxSizing: "border-box",
};

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
      fill="#EA4335"
    />
  </svg>
);

const MicrosoftIcon = () => (
  <svg width="18" height="18" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 1h10v10H1z" fill="#F25022" />
    <path d="M12 1h10v10H12z" fill="#7FBA00" />
    <path d="M1 12h10v10H1z" fill="#00A4EF" />
    <path d="M12 12h10v10H12z" fill="#FFB900" />
  </svg>
);

const LoginPage: React.FC = () => {
  const { isAuthenticated, setToken } = useAdminAuth();
  const navigate = useNavigate();

  const [stage, setStage] = useState<LoginStage>("email");
  const [email, setEmail] = useState("");
  const [availableMethods, setAvailableMethods] = useState<AuthMethod[]>([]);
  const [ssoConnectionId, setSsoConnectionId] = useState<string | null>(null);
  const [stytchOrgId, setStytchOrgId] = useState<string | null>(null);
  const [hasStytch, setHasStytch] = useState(false);

  // Status & feedback
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // OTP state
  const [otpCode, setOtpCode] = useState("");

  // MFA state
  const [mfa, setMfa] = useState<MfaChallenge | null>(null);

  // Admin token fallback state
  const [inputToken, setInputToken] = useState("");
  const [remember, setRemember] = useState(false);
  const [isVerifyingAdminToken, setIsVerifyingAdminToken] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleAuthComplete = (data: any) => {
    if (!completeSsoLogin(data)) {
      setError(INTERNAL_ONLY_MESSAGE);
      return;
    }
    navigate("/", { replace: true });
  };

  // ---------------------------------------------------------------------------
  // Social OAuth Discovery
  // ---------------------------------------------------------------------------
  const handleOAuthDiscovery = (provider: "google" | "microsoft") => {
    if (!stytchClient) {
      setError("SSO is not configured in this environment.");
      return;
    }
    setError(null);
    try {
      localStorage.setItem("pending_oauth_provider", provider);
      const redirectUrl = SSO_CALLBACK_URL;
      if (provider === "google") {
        stytchClient.oauth.google.discovery.start({
          discovery_redirect_url: redirectUrl,
        });
      } else {
        stytchClient.oauth.microsoft.discovery.start({
          discovery_redirect_url: redirectUrl,
        });
      }
    } catch (err) {
      console.error(`Failed to start ${provider} sign-in`, err);
      localStorage.removeItem("pending_oauth_provider");
      setError(`${provider === "google" ? "Google" : "Microsoft"} sign-in is not available.`);
    }
  };

  // ---------------------------------------------------------------------------
  // Email Submit -> check available methods
  // ---------------------------------------------------------------------------
  const handleEmailContinue = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail) {
      setError("Please enter your @coverflow.com email.");
      return;
    }

    if (!isInternalEmail(cleanEmail)) {
      setError(INTERNAL_ONLY_MESSAGE);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const resp = await ssoApi.availableMethods(cleanEmail);
      if (resp.status !== 200) {
        setError("Unable to check login options. Please try again.");
        setLoading(false);
        return;
      }

      const data = resp.body;
      if (!data.found) {
        setError(`No account found for ${cleanEmail}. Please contact your administrator.`);
        setLoading(false);
        return;
      }

      // Internal employees authenticate via SSO / Google OAuth, Email OTP, or Magic Link.
      // Password auth is handled via IdP/SSO and not via Stytch member passwords.
      const rawMethods = (data.methods || ["magic_link"]) as AuthMethod[];
      const methods = rawMethods.filter((m) => (m as string) !== "password");
      if (methods.length === 0) {
        methods.push("email_otp", "magic_link");
      }
      const connId: string | null = data.sso_connection_id || null;
      const orgId: string | null = data.stytch_organization_id || null;

      setAvailableMethods(methods);
      setSsoConnectionId(connId);
      setStytchOrgId(orgId);
      setHasStytch(!!data.has_stytch);

      // Single-method fast path
      if (methods.length === 1 && stytchClient) {
        if (methods[0] === "sso" && connId) {
          stytchClient.sso.start({
            connection_id: connId,
            login_redirect_url: SSO_CALLBACK_URL,
            signup_redirect_url: SSO_CALLBACK_URL,
          });
          return;
        }
        if (methods[0] === "magic_link") {
          await stytchClient.magicLinks.email.discovery.send({
            email_address: cleanEmail,
            discovery_redirect_url: SSO_CALLBACK_URL,
          });
          setStage("magic_link_sent");
          setLoading(false);
          return;
        }
        if (methods[0] === "email_otp") {
          await stytchClient.otps.email.discovery.send({
            email_address: cleanEmail,
          });
          setStage("otp_verify");
          setLoading(false);
          return;
        }
      }

      setStage("methods");
    } catch (err) {
      console.error("Error fetching available methods", err);
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Action Handlers for specific methods
  // ---------------------------------------------------------------------------
  const handleSendMagicLink = async () => {
    if (!stytchClient) return;
    setLoading(true);
    setError(null);
    try {
      await stytchClient.magicLinks.email.discovery.send({
        email_address: email.trim(),
        discovery_redirect_url: SSO_CALLBACK_URL,
      });
      setStage("magic_link_sent");
    } catch (err: any) {
      console.error("[Magic Link] error:", err);
      setError("Could not send login link. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSendOTP = async () => {
    if (!stytchClient) return;
    setLoading(true);
    setError(null);
    try {
      await stytchClient.otps.email.discovery.send({
        email_address: email.trim(),
      });
      setStage("otp_verify");
    } catch (err: any) {
      console.error("[OTP Send Discovery] error:", err);
      setError("Could not send one-time code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleStartSSO = () => {
    if (!stytchClient || !ssoConnectionId) return;
    try {
      stytchClient.sso.start({
        connection_id: ssoConnectionId,
        login_redirect_url: SSO_CALLBACK_URL,
        signup_redirect_url: SSO_CALLBACK_URL,
      });
    } catch (err: any) {
      console.error("[SSO Start] error:", err);
      setError("Could not start SSO redirect. Please contact your administrator.");
    }
  };

  const handleVerifyOTP = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!stytchClient || !otpCode.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const authResp = await stytchClient.otps.email.discovery.authenticate({
        code: otpCode.trim(),
        email_address: email.trim(),
      });

      const ist = authResp.intermediate_session_token;
      if (!ist) {
        throw new Error("No intermediate session token returned from Stytch");
      }

      // Hand the intermediate token to /sso-callback via router state, not the
      // query string, so it never lands in browser history or URL-logging tools.
      // The callback route lists discovered organizations (auto-selecting a single one).
      navigate("/sso-callback", { state: { token: ist, tokenType: "intermediate" } });
    } catch (err: any) {
      console.error("[OTP Discovery verify error]:", err);
      setError(
        err?.error_message || err?.message || "Invalid or expired code. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };



  // ---------------------------------------------------------------------------
  // Fallback: Admin Token
  // ---------------------------------------------------------------------------
  const handleAdminTokenSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputToken.trim()) {
      setError("Please enter an admin token.");
      return;
    }
    setError(null);
    setIsVerifyingAdminToken(true);
    try {
      const resp = await fetch(`${process.env.REACT_APP_BE_URL as string}/admin-validate-token`, {
        method: "POST",
        headers: {
          "admin-token": inputToken.trim(),
          "Content-Type": "application/json",
        },
        // Marks a real login so the backend audits it once, not on every route re-check.
        body: JSON.stringify({ login: true }),
      });

      if (resp.status === 200) {
        setToken(inputToken.trim(), remember);
        navigate("/", { replace: true });
        return;
      }

      clearAdminToken();
      setError(
        resp.status === 403
          ? "Invalid admin token. Please try again."
          : "Unable to validate admin token. Please try again."
      );
    } catch (err) {
      console.error("Failed to validate admin token", err);
      setError("Network error. Please try again.");
    } finally {
      setIsVerifyingAdminToken(false);
    }
  };

  const handleResetToEmail = () => {
    setStage("email");
    setError(null);
    setOtpCode("");
    setMfa(null);
  };

  return (
    <div
      className="login-page-container"
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: UI.BACKGROUND.LIGHT_GREY,
        padding: "clamp(16px, 4vw, 24px)",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
    >
      <div
        className="login-form-wrapper"
        style={{
          width: "100%",
          maxWidth: 420,
          background: UI.BACKGROUND.STANDARD,
          borderRadius: 20,
          border: `1px solid ${UI.BORDER.STANDARD}`,
          boxShadow: "0 12px 32px rgba(24,24,28,0.06)",
          padding: "clamp(32px, 6vw, 44px)",
        }}
      >
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <img
            src={CoverflowLogo}
            alt=""
            width={52}
            height={52}
            style={{ borderRadius: 14, marginBottom: 16 }}
          />
          <h1
            style={{
              fontSize: 24,
              fontWeight: 700,
              color: UI.TEXT.DARK_GREY,
              margin: 0,
              letterSpacing: -0.3,
            }}
          >
            {stage === "otp_verify"
              ? "Enter verification code"
              : stage === "magic_link_sent"
              ? "Check your email"
              : stage === "mfa"
              ? "Two-factor authentication"
              : "Log in to the admin portal"}
          </h1>
          <p style={{ fontSize: 14, color: UI.TEXT.LIGHT, margin: "8px 0 0" }}>
            {stage === "methods"
              ? `Signing in as ${email}`
              : stage === "otp_verify"
              ? `We sent a 6-digit code to ${email}`
              : stage === "magic_link_sent"
              ? `A sign-in link was sent to ${email}`
              : stage === "mfa"
              ? "Enter the code from your authenticator app"
              : "Sign in with your Coverflow account to continue."}
          </p>
        </div>

        {/* Global Error Notice */}
        {error && (
          <div
            style={{
              color: UI.TEXT.DANGER,
              background: "#ffebee",
              borderRadius: 8,
              padding: "10px 14px",
              fontSize: 13,
              marginBottom: 16,
              lineHeight: 1.4,
              textAlign: "center",
            }}
          >
            {error}
          </div>
        )}

        {/* ------------------------------------------------------------------ */}
        {/* Stage 1: Email & Social discovery                                   */}
        {/* ------------------------------------------------------------------ */}
        {stage === "email" && (
          <>
            {stytchClient && (
              <>
                <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
                  <button
                    type="button"
                    onClick={() => handleOAuthDiscovery("google")}
                    disabled={loading}
                    style={secondaryButtonStyle}
                  >
                    <GoogleIcon />
                    <span>Google</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleOAuthDiscovery("microsoft")}
                    disabled={loading}
                    style={secondaryButtonStyle}
                  >
                    <MicrosoftIcon />
                    <span>Microsoft</span>
                  </button>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    margin: "16px 0 20px",
                    color: UI.TEXT.EXTRA_LIGHT,
                    fontSize: 12,
                  }}
                >
                  <div style={{ flex: 1, height: 1, background: UI.BORDER.STANDARD }} />
                  or sign in with email
                  <div style={{ flex: 1, height: 1, background: UI.BORDER.STANDARD }} />
                </div>
              </>
            )}

            <form onSubmit={handleEmailContinue} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <span style={{ fontWeight: 600, fontSize: 13, color: UI.TEXT.STANDARD }}>Email</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError(null);
                  }}
                  placeholder="you@coverflow.com"
                  autoFocus
                  style={inputStyle}
                  disabled={loading}
                />
              </label>

              <button
                type="submit"
                disabled={loading || !email.trim()}
                style={{
                  ...primaryButtonStyle,
                  opacity: loading || !email.trim() ? 0.6 : 1,
                  cursor: loading || !email.trim() ? "not-allowed" : "pointer",
                }}
              >
                {loading ? "Checking options…" : "Continue"}
              </button>
            </form>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                margin: "24px 0 0",
                color: UI.TEXT.EXTRA_LIGHT,
                fontSize: 12,
              }}
            >
              <div style={{ flex: 1, height: 1, background: UI.BORDER.STANDARD }} />
              or
              <div style={{ flex: 1, height: 1, background: UI.BORDER.STANDARD }} />
            </div>

            <details style={{ marginTop: 16, textAlign: "center" }}>
              <summary
                style={{
                  cursor: "pointer",
                  fontSize: 13,
                  color: UI.TEXT.LIGHT,
                  userSelect: "none",
                  listStyle: "none",
                }}
              >
                Use admin token instead
              </summary>

              <form
                onSubmit={handleAdminTokenSubmit}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                  marginTop: 14,
                  textAlign: "left",
                }}
              >
                <input
                  type="password"
                  value={inputToken}
                  onChange={(e) => setInputToken(e.target.value)}
                  placeholder="Paste admin token"
                  style={inputStyle}
                  disabled={isVerifyingAdminToken}
                />

                <label style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    style={{ marginTop: 2 }}
                  />
                  <span style={{ fontSize: 13, color: UI.TEXT.LIGHT }}>Remember token on this device</span>
                </label>

                <button
                  type="submit"
                  disabled={isVerifyingAdminToken}
                  style={{
                    ...primaryButtonStyle,
                    opacity: isVerifyingAdminToken ? 0.7 : 1,
                  }}
                >
                  {isVerifyingAdminToken ? "Verifying..." : "Sign in with admin token"}
                </button>
              </form>
            </details>
          </>
        )}

        {/* ------------------------------------------------------------------ */}
        {/* Stage 2: Dynamic Methods                                           */}
        {/* ------------------------------------------------------------------ */}
        {stage === "methods" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {availableMethods.includes("magic_link") && (
              <button
                type="button"
                onClick={handleSendMagicLink}
                disabled={loading}
                style={secondaryButtonStyle}
              >
                ✉️ Send sign-in link
              </button>
            )}

            {availableMethods.includes("email_otp") && (
              <button
                type="button"
                onClick={handleSendOTP}
                disabled={loading}
                style={secondaryButtonStyle}
              >
                🔢 Send one-time code
              </button>
            )}

            {availableMethods.includes("sso") && ssoConnectionId && (
              <button
                type="button"
                onClick={handleStartSSO}
                disabled={loading}
                style={secondaryButtonStyle}
              >
                🔑 Continue with Corporate SSO
              </button>
            )}



            <div style={{ marginTop: 14, textAlign: "center" }}>
              <button
                type="button"
                onClick={handleResetToEmail}
                style={{
                  background: "none",
                  border: "none",
                  color: UI.TEXT.LIGHT,
                  cursor: "pointer",
                  fontSize: 13,
                  textDecoration: "underline",
                }}
              >
                ← Back to change email
              </button>
            </div>
          </div>
        )}

        {/* ------------------------------------------------------------------ */}
        {/* Stage 3: Magic link sent notification                              */}
        {/* ------------------------------------------------------------------ */}
        {stage === "magic_link_sent" && (
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                fontSize: 14,
                color: UI.TEXT.SUCCESS,
                background: UI.BACKGROUND.SUCCESS,
                borderRadius: 10,
                padding: 16,
                marginBottom: 20,
                lineHeight: 1.5,
              }}
            >
              Check your inbox for a sign-in link. Click it to finish logging in.
            </div>
            <button
              type="button"
              onClick={handleResetToEmail}
              style={{
                background: "none",
                border: "none",
                color: UI.TEXT.LIGHT,
                cursor: "pointer",
                fontSize: 13,
                textDecoration: "underline",
              }}
            >
              ← Back to login
            </button>
          </div>
        )}

        {/* ------------------------------------------------------------------ */}
        {/* Stage 4: One-time Code (OTP) verification                           */}
        {/* ------------------------------------------------------------------ */}
        {stage === "otp_verify" && (
          <form onSubmit={handleVerifyOTP} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
              placeholder="123456"
              autoFocus
              style={{
                ...inputStyle,
                fontSize: 24,
                letterSpacing: 6,
                textAlign: "center",
                padding: "8px 12px",
              }}
            />

            <button
              type="submit"
              disabled={loading || otpCode.trim().length < 6}
              style={{
                ...primaryButtonStyle,
                opacity: loading || otpCode.trim().length < 6 ? 0.6 : 1,
              }}
            >
              {loading ? "Verifying…" : "Verify & Sign In"}
            </button>

            <div style={{ textAlign: "center", marginTop: 8 }}>
              <button
                type="button"
                onClick={handleResetToEmail}
                style={{
                  background: "none",
                  border: "none",
                  color: UI.TEXT.LIGHT,
                  cursor: "pointer",
                  fontSize: 13,
                  textDecoration: "underline",
                }}
              >
                ← Back to login options
              </button>
            </div>
          </form>
        )}

        {/* ------------------------------------------------------------------ */}
        {/* Stage 5: MFA verification / enrollment                              */}
        {/* ------------------------------------------------------------------ */}
        {stage === "mfa" && mfa && (
          <MfaStep challenge={mfa} onSuccess={handleAuthComplete} onBack={handleResetToEmail} />
        )}
      </div>
    </div>
  );
};

export default LoginPage;
